import { Text, VStack } from '@chakra-ui/react';
import { CalculatePriceResponse } from 'api/responseTypes';
import { usePromoCode } from './hooks/usePromoCode';
import { AppliedPromoCode } from './components/AppliedPromoCode';
import { DiscountApplied } from './components/DiscountApplied';
import { PromoCodeInput } from './components/PromoCodeInput';
import { PromoCodeStatus } from '../PromoCodeStatus/PromoCodeStatus';
import { PromoCodeSkeleton } from './components/PromoCodeSkeleton';

type PromoCodeProps = {
  priceCalculation?: CalculatePriceResponse | null;
  onPriceCalculated?: (response: CalculatePriceResponse) => void;
  onPromoCodeRemoved?: () => void;
  onAuthRequired?: () => void;
};

export const PromoCode = ({
  priceCalculation,
  onPriceCalculated,
  onPromoCodeRemoved,
  onAuthRequired,
}: PromoCodeProps) => {
  const {
    displayPromoCode,
    inputValue,
    isLoading,
    isRemoving,
    isValidatingForNewEvent,
    error,
    handleApply,
    handleRemove,
    handleInputChange,
    handleKeyDown,
  } = usePromoCode({ onPriceCalculated, onPromoCodeRemoved, onAuthRequired, priceCalculation });

  const hasDiscount =
    priceCalculation?.appliedPromoCodesAmount && priceCalculation.appliedPromoCodesAmount > 0;

  const shouldShowSkeleton =
    isValidatingForNewEvent || (displayPromoCode && !priceCalculation && !isLoading && !isRemoving);

  return (
    // No "رمز الخصم" heading: the design carries the affordance in the field itself (tag icon +
    // placeholder), so a label above it was redundant. `checkout.promoCode.label` is now orphaned —
    // noted for TZ-CO-19.
    <VStack spacing='8px' align='stretch' w='100%'>
      {shouldShowSkeleton ? (
        <PromoCodeSkeleton />
      ) : displayPromoCode ? (
        <VStack spacing='8px' align='stretch' w='100%'>
          {hasDiscount && !isRemoving && (
            <DiscountApplied appliedPromoCodesAmount={priceCalculation.appliedPromoCodesAmount} />
          )}
          <AppliedPromoCode
            promoCode={displayPromoCode}
            priceCalculation={priceCalculation}
            onRemove={handleRemove}
            isRemoving={isRemoving}
          />
          {hasDiscount && !isRemoving && <PromoCodeStatus priceCalculation={priceCalculation} />}
        </VStack>
      ) : (
        <PromoCodeInput
          value={inputValue}
          isLoading={isLoading || isRemoving}
          error={error}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onApply={handleApply}
        />
      )}

      {error && (
        <Text variant='bodySmallRegular' color='red' fontWeight='500' textAlign='end' role='alert'>
          {error}
        </Text>
      )}
    </VStack>
  );
};
